import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to login
        </Link>

        <article className="prose prose-neutral dark:prose-invert max-w-none prose-headings:font-display prose-p:text-muted-foreground prose-li:text-muted-foreground">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            Terms of Service — ReviewBoost Pro Manager
          </h1>
          <p className="text-sm text-muted-foreground">Last updated: 8 March 2026</p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">1. Agreement to terms</h2>
          <p>
            By accessing or using ReviewBoost Pro Manager (&quot;Service&quot;) at app.reviewboost24.com, you agree to
            be bound by these Terms of Service and all applicable laws, including those of the Republic of Zambia. If
            you do not agree, do not use the Service.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">2. Description of service</h2>
          <p>
            ReviewBoost Pro Manager is an AI-driven Business Intelligence platform for retail and service businesses.
            It provides dashboards, inventory and sales tracking, expense logging, reports, AI-generated Strategic
            Directives, and team management. Insights (e.g. Inventory Velocity, Inventory Value) are based on
            user-provided data and are for informational use only.
          </p>
          <p className="rounded-lg border border-border bg-muted/50 p-4 text-sm mt-4">
            <strong>Platform and payment boundary:</strong> ReviewBoost Pro Manager is a software platform and is not a
            bank, payment processor, or financial institution. All financial transactions (including subscriptions and
            payments) are conducted through Lenco and are subject solely to Lenco&apos;s regulatory compliance and
            licensing.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">3. Account and use</h2>
          <p>
            You must provide accurate information and keep your login credentials secure. You are responsible for
            all activity under your account. You must use the Service only for lawful purposes and in accordance
            with these Terms. You may not misuse the Service (e.g. attempt to gain unauthorised access, introduce
            malware, or scrape data in bulk without permission).
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">4. No professional advice</h2>
          <p>
            The Strategic Directives and other AI-generated or analytical content are for informational purposes
            only and do not constitute financial, legal, or professional advice. We are not responsible for
            business decisions you make based on the Service. You should seek qualified advice where appropriate.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">5. Intellectual property</h2>
          <p>
            We (or our licensors) own all rights in the Service, including software, design, and branding. We grant
            you a limited, non-exclusive, non-transferable licence to use the Service for your internal business
            use. You retain ownership of your business data; you grant us the licence necessary to operate the
            Service (e.g. to store, process, and display your data).
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">6. Payments and pricing — ReviewBoost Pro Manager</h2>

          <h3 className="mt-4 text-base font-semibold text-foreground">6.1 Our subscription plans and pricing</h3>
          <p>
            ReviewBoost Pro Manager offers subscription plans including Pro Solo (1 location) and Pro Growth (up to 3
            locations). Current pricing is displayed on our Pricing page and in-app, in Zambian Kwacha (ZMW). Standard
            rates include Pro Solo (e.g. K900/month, K8,500/year) and Pro Growth (e.g. K1,800/month, K17,000/year).
            Limited founding-member pricing may apply. We may update pricing with reasonable notice; changes apply to new
            subscriptions or renewals after the effective date.
          </p>

          <h3 className="mt-4 text-base font-semibold text-foreground">6.2 Billing and renewal</h3>
          <p>
            Subscriptions are billed monthly or annually according to your chosen plan. Your subscription renews
            automatically unless you cancel. You are responsible for ensuring your payment method is valid. If payment
            fails, we provide a 3-day grace period during which full Pro access continues while you update your payment
            method.
          </p>

          <h3 className="mt-4 text-base font-semibold text-foreground">6.3 Cancellation and refunds</h3>
          <p>
            You may cancel your subscription at any time from Settings. Access continues until the end of your current
            billing period. We do not provide prorated refunds for partial periods. If ReviewBoost Pro does not deliver
            value in your first 30 days, contact us—we may, at our discretion, offer a refund or accommodation.
          </p>

          <h3 className="mt-4 text-base font-semibold text-foreground">6.4 Third-party payment processor (Lenco)</h3>
          <p>
            Payment processing for ReviewBoost Pro Manager is facilitated through{" "}
            <a href="https://lenco.co/zm" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Lenco</a>.
            By subscribing or making a payment, you acknowledge that:
          </p>
          <ul className="list-disc pl-6 mt-2 space-y-1 text-muted-foreground">
            <li>Payments are processed by Lenco and subject to Lenco&apos;s terms, privacy policy, and fee structure.</li>
            <li>ReviewBoost does not operate as a bank or payment institution; Lenco is the licensed payment service provider.</li>
            <li>Refunds and payment disputes involving the payment mechanism are governed by Lenco&apos;s policies and applicable law.</li>
          </ul>
          <p className="mt-4">
            For Lenco&apos;s legal documents and payment terms, visit{" "}
            <a href="https://lenco.co/zm" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://lenco.co/zm</a>.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">7. Disclaimers</h2>
          <p>
            The Service is provided &quot;as is&quot; and &quot;as available.&quot; We disclaim all warranties,
            express or implied, including merchantability and fitness for a particular purpose. We do not warrant
            that the Service will be uninterrupted, error-free, or free of harmful components.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">8. Limitation of liability</h2>
          <p>
            To the fullest extent permitted by law, we (and our affiliates, directors, and employees) shall not be
            liable for any indirect, incidental, special, consequential, or punitive damages, or for loss of
            profits, data, or goodwill, arising from your use or inability to use the Service. Our total liability
            for any claims arising from or related to the Service shall not exceed the amount you paid us in the
            twelve (12) months preceding the claim (or one hundred United States dollars, whichever is greater).
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">9. Indemnity</h2>
          <p>
            You agree to indemnify and hold us harmless from any claims, damages, or expenses (including reasonable
            legal fees) arising from your use of the Service, your breach of these Terms, or your violation of any
            law or third-party rights.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">10. Termination</h2>
          <p>
            We may suspend or terminate your access to the Service at any time for breach of these Terms or for
            any other reason. You may stop using the Service at any time. Upon termination, your right to use the
            Service ceases. Provisions that by their nature should survive (e.g. disclaimers, limitation of
            liability, indemnity) will survive termination.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">11. Changes to terms or service</h2>
          <p>
            We may modify these Terms or the Service from time to time. We will post updated Terms on this page and
            update the &quot;Last updated&quot; date. Material changes may be communicated via the Service or email.
            Continued use after the effective date of changes constitutes acceptance. If you do not agree, you must
            stop using the Service.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">12. Governing law and disputes</h2>
          <p>
            These Terms are governed by the laws of the Republic of Zambia. Any dispute arising from or related to
            these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of Zambia,
            unless otherwise required by mandatory law.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">13. Contact</h2>
          <p>
            For questions about these Terms: support@reviewboost24.com. App: https://app.reviewboost24.com. Address:
            Lusaka, Zambia. Support phone: +260 770347527.
          </p>
        </article>
      </div>
    </div>
  );
}
